import { Link } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { PRIVACY_PAGE_SEO } from "@/constants/seoConfig";

export default function PrivacyPage() {
  const privacySeo = useMemo(() => PRIVACY_PAGE_SEO, []);
  useSEO(privacySeo);
  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section
          className="pt-28 pb-10 px-4 md:px-8 relative overflow-hidden"
          style={{ background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #faf8ff 100%)" }}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute w-96 h-96 rounded-full opacity-20 -top-20 -right-20" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)" }} />
            <div className="absolute w-64 h-64 rounded-full opacity-15 bottom-0 left-10" style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)" }} />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <nav aria-label="Breadcrumb" className="flex justify-center mb-4">
              <ol className="flex items-center gap-2 text-sm text-[#7a6aaa]">
                <li><Link to="/" className="hover:text-[#7C3AED] transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-[#7C3AED] font-medium">Privacy Policy</li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
              <i className="ri-shield-check-line"></i>
              Legal
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-[#5a4a8a] text-base max-w-xl mx-auto">
              Last updated: April 28, 2026
            </p>
          </div>
        </section>

        {/* Blend: gradient to white */}
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />

        <div className="bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-14">
            <div className="space-y-8 text-[#5a4a8a] text-sm leading-relaxed">
              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">1. Introduction</h2>
                <p>
                  At Neuroava LLC ("we," "us," or "our"), we take your privacy seriously. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use the Ava Chat AI application and website.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">2. Information We Collect</h2>
                <p className="mb-2">We collect the following types of information:</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong className="text-[#1a1040]">Account Information:</strong> Email address, name, and profile information when you create an account.</li>
                  <li><strong className="text-[#1a1040]">Conversation Data:</strong> The messages you send to Ava and her responses. This data is used to provide and improve our services.</li>
                  <li><strong className="text-[#1a1040]">Usage Data:</strong> App usage statistics, feature interactions, and performance data to help us improve Ava.</li>
                  <li><strong className="text-[#1a1040]">Device Information:</strong> Device type, operating system, and app version for technical support and compatibility.</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">3. How We Use Your Information</h2>
                <p className="mb-2">We use your information to:</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Provide and maintain the Ava service</li>
                  <li>Personalize your experience and improve Ava's responses</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Send important service updates and optional newsletters</li>
                  <li>Detect and prevent fraud, abuse, or security issues</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">4. Data Security</h2>
                <p>
                  We implement industry-standard security measures to protect your data, including encryption in transit and at rest. Your conversations with Ava are encrypted and stored securely. We regularly review and update our security practices.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">5. We Do Not Sell Your Data</h2>
                <p>
                  <strong className="text-[#1a1040]">We never sell, rent, or share your personal conversations or data with third parties for advertising or marketing purposes.</strong> Your relationship with Ava is private. Period.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">6. Data Retention</h2>
                <p>
                  We retain your conversation data for as long as your account is active to maintain continuity in your relationship with Ava. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">7. Your Rights</h2>
                <p className="mb-2">Depending on your location, you may have the right to:</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Access the personal data we hold about you</li>
                  <li>Correct inaccurate or incomplete data</li>
                  <li>Delete your personal data</li>
                  <li>Export your data</li>
                  <li>Object to certain processing activities</li>
                  <li>Withdraw consent at any time</li>
                </ul>
                <p className="mt-2">
                  To exercise these rights, contact us at <a href="mailto:info@neuroava.com" className="text-[#7C3AED] hover:underline">info@neuroava.com</a>.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">8. Children's Privacy</h2>
                <p>
                  Ava is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">9. Third-Party Services</h2>
                <p>
                  We use trusted third-party services for payment processing (Apple App Store, Google Play Store) and analytics. These services have their own privacy policies and are bound by strict data protection agreements.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">10. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of significant changes through the app or email. The "Last updated" date at the top of this page indicates when changes were made.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">11. Contact Us</h2>
                <p className="mb-2">If you have any questions or concerns about this Privacy Policy, please contact us:</p>
                <div className="bg-white rounded-xl p-5 border border-[#e8e0ff] mt-3">
                  <p className="mb-1"><strong className="text-[#1a1040]">Neuroava LLC</strong></p>
                  <p className="mb-1">United States</p>
                  <p className="mb-1">Email: <a href="mailto:info@neuroava.com" className="text-[#7C3AED] hover:underline">info@neuroava.com</a></p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Blend: white to gradient */}
      <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
      <Footer />
    </div>
  );
}